import { useRecoilState, useSetRecoilState } from 'recoil'
import { LoginVO, passwordExpiryDateAtom, sessionAtom, SessionVO } from '../store/auth.store'
import { refreshToken } from '../repositories/token/TokenRepository'
import { deleteCookie, getCookie, setCookie } from '../util/cookie'
import { loginV2, logoutRequest } from '../repositories/login/LoginRepository'
import { LoginForm } from '../components/intro/useLoginForm'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useLoginNoticeStore } from '../feature/loginNotice/loginNoticeStore'

const setSessionCookie = ({ token, expiredDateTime }: SessionVO) => {
  setCookie('token', token)
  setCookie('expiredDateTime', expiredDateTime)
}

const setAuthCookie = (loginInfo: LoginVO) => {
  const { token, expiredDateTime, passwordExpiryDate } = loginInfo
  setSessionCookie({ token, expiredDateTime })
  setCookie('passwordExpiryDate', passwordExpiryDate)
}

const useAuth = () => {
  const [isCoolDown, setIsCoolDown] = useState(false)
  const [session, setSession] = useRecoilState(sessionAtom)
  const setPasswordExpiryDate = useSetRecoilState(passwordExpiryDateAtom)
  const navigate = useNavigate()

  const refreshSession = async () => {
    const {
      data: { body },
    } = await refreshToken()
    setSession(body)
  }

  const refreshSessionWithCoolDown = async () => {
    if (isCoolDown) return
    setIsCoolDown(true)
    await refreshSession()
    setIsCoolDown(false)
  }

  const checkPasswordExpired = (): boolean => {
    const passwordExpiryDate = getCookie('passwordExpiryDate')
    const today = dayjs()
    return today.isAfter(dayjs(passwordExpiryDate), 'day')
  }

  // redirect 경로 찾기
  const findRedirectUrl = () =>
    (new URLSearchParams(location.search).get('redirect') ?? '/').replace(/tanent/g, 'tenant')

  const login = async (form: LoginForm, redirectUrlAfterLogin: string) => {
    const {
      data: { body },
    } = await loginV2(form)
    const { token, expiredDateTime, passwordExpiryDate, passwordChangeRequired } = body

    setAuthCookie(body)
    setPasswordExpiryDate(passwordExpiryDate)
    setSession({ token, expiredDateTime })
    if (body.failCountBeforeLogin !== undefined) {
      useLoginNoticeStore.getState().show({
        previousLoginDateTime: body.previousLoginDateTime ?? null,
        previousLoginIp: body.previousLoginIp ?? null,
        failCountBeforeLogin: body.failCountBeforeLogin,
        loginFailures: body.loginFailures ?? [],
      })
    }

    // 임시 비밀번호이거나 만료일이 지났으면 비밀번호 재설정 페이지로 이동
    if (passwordChangeRequired || checkPasswordExpired()) {
      navigate({ pathname: '/account/reset-password', search: `?type=expired&redirect=${redirectUrlAfterLogin}` })
    } else {
      navigate(findRedirectUrl())
    }
  }

  const logout = () => {
    // 서버 세션을 끊고 접속기록(로그아웃)을 남긴다. 실패해도 화면 로그아웃은 진행한다
    if (getCookie('token')) {
      logoutRequest().catch(() => undefined)
    }
    deleteCookie('token')
    deleteCookie('expiredDateTime')
    deleteCookie('passwordExpiryDate')
    setSession(null)
  }

  const backToLogin = (redirectUrl: string) => {
    location.href = `/login?redirect=${redirectUrl}`
  }

  return {
    session,
    refreshSession,
    refreshSessionWithCoolDown,
    login,
    logout,
    backToLogin,
    findRedirectUrl,
    isCoolDown,
  }
}
export default useAuth
